import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from './Layout/DashboardLayout';
import './AdminAIDashboard.css';
import FinancialConsultantChat from './FinancialConsultantChat';

const AdminAIDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [riskData, setRiskData] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const [tokenLeaderboard, setTokenLeaderboard] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [filterUserId, setFilterUserId] = useState('');
  const [skills, setSkills] = useState([]);
  const [editingSkill, setEditingSkill] = useState(null); // skill object being created/edited
  const emptySkill = { id: 0, name: '', category: 'fitness', triggers: '', systemPromptAddition: '', isActive: true };

  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  const loadTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await api.get('/AdminAI/statistics');
        setStats(res.data);
      } else if (activeTab === 'risk') {
        const res = await api.get('/AdminAI/risk-analysis');
        setRiskData(res.data);
      } else if (activeTab === 'chats') {
        const url = filterUserId ? `/AdminAI/all-chats?userId=${filterUserId}` : '/AdminAI/all-chats';
        const res = await api.get(url);
        setAllChats(res.data);
      } else if (activeTab === 'tokens') {
        const res = await api.get('/AdminAI/token-leaderboard');
        setTokenLeaderboard(res.data);
      } else if (activeTab === 'settings') {
        const res = await api.get('/AdminAI/settings');
        setSettings(res.data);
      } else if (activeTab === 'skills') {
        const res = await api.get('/AdminAI/skills');
        setSkills(res.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeUser = async (userId) => {
    try {
      toast.info('A analisar utilizador...');
      await api.post(`/AdminAI/analyze-user/${userId}`);
      toast.success('Análise concluída!');
      loadTabData();
    } catch (error) {
      toast.error('Erro ao analisar utilizador');
    }
  };

  const analyzeAllUsers = async () => {
    setAnalyzingAll(true);
    try {
      toast.info('A analisar todos os utilizadores... isto pode demorar.');
      const res = await api.post('/AdminAI/analyze-all-users');
      toast.success(`Análise concluída! ${res.data.totalAnalyzed} analisados. Alto: ${res.data.highRisk}, Médio: ${res.data.mediumRisk}, Baixo: ${res.data.lowRisk}`);
      loadTabData();
    } catch (error) {
      toast.error('Erro ao analisar utilizadores');
    } finally {
      setAnalyzingAll(false);
    }
  };

  const saveSettings = async () => {
    try {
      await api.put('/AdminAI/settings', settings);
      toast.success('Configurações guardadas com sucesso!');
    } catch (error) {
      toast.error('Erro ao guardar configurações');
    }
  };

  // ----- Skills (conhecimento especializado da IA) -----
  const saveSkill = async () => {
    if (!editingSkill.name || !editingSkill.triggers || !editingSkill.systemPromptAddition) {
      toast.error('Nome, triggers e conhecimento são obrigatórios');
      return;
    }
    try {
      if (editingSkill.id && editingSkill.id > 0) {
        await api.put(`/AdminAI/skills/${editingSkill.id}`, editingSkill);
        toast.success('Skill atualizada!');
      } else {
        await api.post('/AdminAI/skills', editingSkill);
        toast.success('Skill criada!');
      }
      setEditingSkill(null);
      loadTabData();
    } catch (error) {
      toast.error('Erro ao guardar skill');
    }
  };

  const toggleSkill = async (id) => {
    try {
      await api.patch(`/AdminAI/skills/${id}/toggle`);
      loadTabData();
    } catch (error) {
      toast.error('Erro ao alterar estado da skill');
    }
  };

  const deleteSkill = async (id, name) => {
    if (!window.confirm(`Apagar a skill "${name}"? Esta ação não pode ser revertida.`)) return;
    try {
      await api.delete(`/AdminAI/skills/${id}`);
      toast.success('Skill removida');
      loadTabData();
    } catch (error) {
      toast.error('Erro ao remover skill');
    }
  };

  const seedDefaultSkills = async () => {
    try {
      const res = await api.post('/AdminAI/skills/seed-defaults');
      toast.success(`${res.data.skillsAdded} skills padrão adicionadas`);
      loadTabData();
    } catch (error) {
      toast.error('Erro ao adicionar skills padrão');
    }
  };

  const awardTokens = async (userId, userName) => {
    const amount = prompt(`Quantos tokens atribuir a ${userName}?`, '100');
    if (!amount) return;
    const reason = prompt('Motivo:', 'Atribuição manual pelo administrador');
    try {
      await api.post('/AdminAI/award-tokens', { userId, amount: parseInt(amount), reason: reason || 'Admin award' });
      toast.success(`${amount} tokens atribuídos a ${userName}!`);
      loadTabData();
    } catch (error) {
      toast.error('Erro ao atribuir tokens');
    }
  };

  const getRiskIcon = (level) => {
    switch (level) { case 'Alto': return '🔴'; case 'Médio': return '🟡'; case 'Baixo': return '🟢'; default: return '⚪'; }
  };

  const getRiskColor = (level) => {
    switch (level) { case 'Alto': return '#e74c3c'; case 'Médio': return '#f39c12'; case 'Baixo': return '#27ae60'; default: return '#95a5a6'; }
  };

  return (
    <DashboardLayout userType="admin">
      <div className="admin-ai-page">
        <div className="admin-ai-title">
          <h1>🤖 MMIA — Painel de Inteligência Artificial</h1>
          <p>Análise comportamental, gestão de tokens, configurações e monitorização</p>
        </div>

        <div className="ai-tabs">
          {[
            { key: 'dashboard', icon: '📊', label: 'Dashboard' },
            { key: 'risk', icon: '⚠️', label: 'Análise de Risco' },
            { key: 'chats', icon: '💬', label: 'Conversas IA' },
            { key: 'consultor', icon: '💰', label: 'Consultor IA' },
            { key: 'skills', icon: '🧠', label: 'Skills (Conhecimento)' },
            { key: 'tokens', icon: '🪙', label: 'Tokens' },
            { key: 'settings', icon: '⚙️', label: 'Funcionalidades' },
          ].map(tab => (
            <button key={tab.key} className={`ai-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>

        <div className="ai-tab-content">
          {loading && <div className="ai-loading"><div className="spinner"></div></div>}

          {/* DASHBOARD */}
          {activeTab === 'dashboard' && stats && !loading && (
            <div className="ai-dashboard">
              <div className="ai-stats-grid">
                <div className="ai-stat-card purple"><div className="ai-stat-icon">💬</div><div className="ai-stat-value">{stats.totalChats}</div><div className="ai-stat-label">Total Interações</div></div>
                <div className="ai-stat-card blue"><div className="ai-stat-icon">👥</div><div className="ai-stat-value">{stats.uniqueUsers}</div><div className="ai-stat-label">Utilizadores Únicos</div></div>
                <div className="ai-stat-card green"><div className="ai-stat-icon">🏋️</div><div className="ai-stat-value">{stats.totalWorkoutPlans}</div><div className="ai-stat-label">Planos de Treino</div></div>
                <div className="ai-stat-card orange"><div className="ai-stat-icon">🥗</div><div className="ai-stat-value">{stats.totalMealPlans}</div><div className="ai-stat-label">Planos Alimentares</div></div>
              </div>
              <div className="ai-service-status">
                <span className={`status-dot ${stats.serviceStatus === 'Online' ? 'online' : 'offline'}`}></span>
                Serviço de IA (MMIA): <strong>{stats.serviceStatus}</strong>
              </div>
              <div className="ai-section">
                <h3>🏆 Top Utilizadores (Engagement)</h3>
                {stats.topUsers && stats.topUsers.length > 0 ? (
                  <table className="ai-table">
                    <thead><tr><th>#</th><th>Utilizador</th><th>Mensagens</th><th>Última Atividade</th><th>Ações</th></tr></thead>
                    <tbody>
                      {stats.topUsers.map((u, i) => (
                        <tr key={u.userId}>
                          <td>{i + 1}</td>
                          <td><strong>{u.userName}</strong></td>
                          <td>{u.messageCount}</td>
                          <td>{new Date(u.lastMessage).toLocaleDateString('pt-PT')}</td>
                          <td>
                            <button className="btn-sm btn-info" onClick={() => analyzeUser(u.userId)}>Analisar</button>
                            <button className="btn-sm btn-chat" onClick={() => { setFilterUserId(u.userId); setActiveTab('chats'); }}>Ver Chat</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (<p className="no-data">Sem dados de engagement</p>)}
              </div>
            </div>
          )}

          {/* ANÁLISE DE RISCO */}
          {activeTab === 'risk' && !loading && (
            <div className="ai-risk">
              <div className="ai-risk-header">
                <h3>⚠️ Análise Comportamental de Risco</h3>
                <button className="btn-primary" onClick={analyzeAllUsers} disabled={analyzingAll}>
                  {analyzingAll ? '⏳ A analisar...' : '🔍 Analisar Todos'}
                </button>
              </div>
              {riskData.length === 0 ? (
                <div className="no-data-box"><p>🔍 Nenhuma análise realizada. Clique "Analisar Todos" para começar.</p></div>
              ) : (
                <>
                  <div className="risk-summary">
                    <div className="risk-count alto"><span className="risk-num">{riskData.filter(r => r.riskLevel === 'Alto').length}</span><span>Alto Risco</span></div>
                    <div className="risk-count medio"><span className="risk-num">{riskData.filter(r => r.riskLevel === 'Médio').length}</span><span>Médio Risco</span></div>
                    <div className="risk-count baixo"><span className="risk-num">{riskData.filter(r => r.riskLevel === 'Baixo').length}</span><span>Baixo Risco</span></div>
                  </div>
                  <div className="risk-cards">
                    {riskData.map(r => (
                      <div key={r.id} className={`risk-card risk-${r.riskLevel.toLowerCase()}`}>
                        <div className="risk-card-header">
                          <span className="risk-icon">{getRiskIcon(r.riskLevel)}</span>
                          <div><strong>{r.userName}</strong><span className="risk-email">{r.userEmail}</span></div>
                          <span className="risk-badge" style={{ backgroundColor: getRiskColor(r.riskLevel) }}>{r.riskLevel}</span>
                        </div>
                        <div className="risk-card-metrics">
                          <div><label>Dias Inativo:</label> <span>{r.daysInactive}</span></div>
                          <div><label>Taxa Presença:</label> <span>{r.attendanceRate?.toFixed(1)}%</span></div>
                          <div><label>Notificação:</label> <span>{r.notificationSent ? '✅ Enviada' : '❌ Pendente'}</span></div>
                          <div><label>Analisado:</label> <span>{new Date(r.analyzedAt).toLocaleDateString('pt-PT')}</span></div>
                        </div>
                        {r.recommendations && (
                          <div className="risk-card-recommendations"><h5>💡 Recomendações IA:</h5><p>{r.recommendations}</p></div>
                        )}
                        <button className="btn-sm btn-info" onClick={() => analyzeUser(r.userId)}>🔄 Re-analisar</button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* CONVERSAS IA */}
          {activeTab === 'chats' && !loading && (
            <div className="ai-chats">
              <div className="ai-chats-header">
                <h3>💬 Conversas com IA</h3>
                <div className="chats-filter">
                  <input type="number" placeholder="ID utilizador..." value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)} />
                  <button className="btn-sm btn-primary" onClick={loadTabData}>Filtrar</button>
                  {filterUserId && <button className="btn-sm btn-secondary" onClick={() => { setFilterUserId(''); setTimeout(loadTabData, 100); }}>Limpar</button>}
                </div>
              </div>
              {allChats.length === 0 ? (<p className="no-data">Sem conversas</p>) : (
                <div className="chats-list">
                  {allChats.map(chat => (
                    <div key={chat.id} className={`chat-item ${chat.isFromUser ? 'from-user' : 'from-ai'}`}>
                      <div className="chat-item-header">
                        <span className="chat-user">{chat.isFromUser ? '👤' : '🤖'} {chat.userName}</span>
                        <span className="chat-date">{new Date(chat.createdAt).toLocaleString('pt-PT')}</span>
                      </div>
                      <div className="chat-item-message">{chat.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CONSULTOR FINANCEIRO IA */}
          {activeTab === 'consultor' && !loading && (
            <div className="ai-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: 0 }}>💰 Consultor Financeiro IA</h3>
                  <p className="settings-desc" style={{ marginTop: 6 }}>Chat financeiro com análise real de vendas, compras, pendentes e documentos (PDF/abrir).</p>
                </div>
                <button
                  className="btn-sm btn-primary"
                  onClick={() => navigate('/admin/consultor-financeiro')}
                  title="Abrir em ecrã inteiro"
                >
                  ⛶ Ecrã inteiro
                </button>
              </div>
              <FinancialConsultantChat conversationId="admin-ai-trainer" />
            </div>
          )}

          {/* SKILLS — CONHECIMENTO ESPECIALIZADO DA IA */}
          {activeTab === 'skills' && !loading && (
            <div className="ai-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: 0 }}>🧠 Skills — Conhecimento Especializado</h3>
                  <p className="settings-desc" style={{ marginTop: 6 }}>
                    Ensina a IA sobre temas de ginásio. Quando a mensagem do aluno contém um dos <em>triggers</em>,
                    o conhecimento da skill é injetado automaticamente no Personal Trainer.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-sm btn-secondary" onClick={seedDefaultSkills} title="Adicionar as 8 skills padrão">+ Skills padrão</button>
                  <button className="btn-sm btn-primary" onClick={() => setEditingSkill({ ...emptySkill })}>+ Nova Skill</button>
                </div>
              </div>

              {editingSkill && (
                <div className="skill-editor" style={{ background: '#f8f9ff', border: '1px solid #e0e3f0', borderRadius: 12, padding: 16, margin: '16px 0' }}>
                  <h4 style={{ marginTop: 0 }}>{editingSkill.id > 0 ? '✏️ Editar Skill' : '➕ Nova Skill'}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600 }}>
                      Nome
                      <input type="text" value={editingSkill.name} placeholder="Ex: Nutrição Desportiva"
                        onChange={(e) => setEditingSkill({ ...editingSkill, name: e.target.value })}
                        style={{ padding: 8, borderRadius: 8, border: '1px solid #ccc', marginTop: 4, fontWeight: 400 }} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600 }}>
                      Categoria
                      <select value={editingSkill.category}
                        onChange={(e) => setEditingSkill({ ...editingSkill, category: e.target.value })}
                        style={{ padding: 8, borderRadius: 8, border: '1px solid #ccc', marginTop: 4, fontWeight: 400 }}>
                        <option value="treino">Treino</option>
                        <option value="nutricao">Nutrição</option>
                        <option value="recuperacao">Recuperação</option>
                        <option value="lesoes">Lesões</option>
                        <option value="motivacao">Motivação</option>
                        <option value="fitness">Geral</option>
                      </select>
                    </label>
                  </div>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600, marginTop: 12 }}>
                    Triggers (palavras-chave separadas por vírgula)
                    <input type="text" value={editingSkill.triggers} placeholder="hipertrofia, massa muscular, ganhar músculo"
                      onChange={(e) => setEditingSkill({ ...editingSkill, triggers: e.target.value })}
                      style={{ padding: 8, borderRadius: 8, border: '1px solid #ccc', marginTop: 4, fontWeight: 400 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600, marginTop: 12 }}>
                    Conhecimento a injetar no prompt
                    <textarea rows={6} value={editingSkill.systemPromptAddition} placeholder="Instruções e conhecimento que a IA deve usar quando esta skill é ativada..."
                      onChange={(e) => setEditingSkill({ ...editingSkill, systemPromptAddition: e.target.value })}
                      style={{ padding: 8, borderRadius: 8, border: '1px solid #ccc', marginTop: 4, fontWeight: 400, fontFamily: 'inherit' }} />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
                    <input type="checkbox" checked={editingSkill.isActive}
                      onChange={(e) => setEditingSkill({ ...editingSkill, isActive: e.target.checked })} />
                    Ativa
                  </label>
                  <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                    <button className="btn-sm btn-primary" onClick={saveSkill}>💾 Guardar</button>
                    <button className="btn-sm btn-secondary" onClick={() => setEditingSkill(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              <table className="ai-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr><th>Estado</th><th>Nome</th><th>Categoria</th><th>Triggers</th><th>Ações</th></tr>
                </thead>
                <tbody>
                  {skills.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#888' }}>Sem skills. Clica em "+ Skills padrão" para começar.</td></tr>
                  )}
                  {skills.map(s => (
                    <tr key={s.id} style={{ opacity: s.isActive ? 1 : 0.5 }}>
                      <td>
                        <span onClick={() => toggleSkill(s.id)} style={{ cursor: 'pointer' }} title="Clica para ativar/desativar">
                          {s.isActive ? '🟢 Ativa' : '⚪ Inativa'}
                        </span>
                      </td>
                      <td><strong>{s.name}</strong></td>
                      <td>{s.category}</td>
                      <td style={{ fontSize: 12, color: '#666', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.triggers}>{s.triggers}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn-sm btn-secondary" onClick={() => setEditingSkill({ ...s })}>✏️</button>{' '}
                        <button className="btn-sm btn-danger" onClick={() => deleteSkill(s.id, s.name)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TOKENS */}
          {activeTab === 'tokens' && !loading && (
            <div className="ai-tokens">
              <h3>🪙 Economia de Tokens</h3>
              <div className="token-info-box">
                <h4>ℹ️ Como funcionam os Tokens</h4>
                <div className="token-rules">
                  <div>✅ Reserva completada: <strong>+10</strong></div>
                  <div>🔥 Streak 7 dias: <strong>+50</strong></div>
                  <div>🏆 Streak 30 dias: <strong>+200</strong></div>
                  <div>💬 Primeiro chat IA: <strong>+20</strong></div>
                  <div>🏋️ Plano treino: <strong>+15</strong></div>
                  <div>🥗 Plano alimentar: <strong>+15</strong></div>
                </div>
              </div>
              {tokenLeaderboard.length === 0 ? (<p className="no-data">Sem dados</p>) : (
                <table className="ai-table">
                  <thead><tr><th>#</th><th>Utilizador</th><th>Saldo</th><th>Ganho</th><th>Gasto</th><th>Ações</th></tr></thead>
                  <tbody>
                    {tokenLeaderboard.map((t, i) => (
                      <tr key={t.userId}>
                        <td>{i + 1}</td>
                        <td><strong>{t.userName}</strong></td>
                        <td className="token-balance">🪙 {t.balance}</td>
                        <td className="token-earned">+{t.totalEarned}</td>
                        <td className="token-spent">-{t.totalSpent}</td>
                        <td><button className="btn-sm btn-success" onClick={() => awardTokens(t.userId, t.userName)}>+ Atribuir</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* FUNCIONALIDADES / FEATURE TOGGLES */}
          {activeTab === 'settings' && settings && !loading && (
            <div className="ai-settings">
              <h3>⚙️ Funcionalidades do Sistema</h3>
              <p className="settings-desc">Active ou desative funcionalidades. As alterações têm efeito imediato após guardar.</p>
              <div className="feature-toggles">
                {[
                  { key: 'aiPersonalTrainerEnabled', icon: '🤖', title: 'AI Personal Trainer (MMIA)', desc: 'Alunos podem conversar com a IA, pedir planos de treino e planos alimentares personalizados.' },
                  { key: 'behaviorAnalysisEnabled', icon: '📊', title: 'Análise Comportamental', desc: 'Analisa frequência, comportamento e padrões. Deteta alunos em risco de desistência.' },
                  { key: 'autoNotificationsEnabled', icon: '🔔', title: 'Notificações Automáticas de Risco', desc: 'Alertas automáticos: "O João está em risco de desistir, aborda-o com um novo desafio".' },
                  { key: 'tokenSystemEnabled', icon: '🪙', title: 'Economia de Tokens', desc: 'Moedas virtuais por consistência. Podem trocar por recompensas. Cria comunidade e retenção.' },
                  { key: 'crmAutomationEnabled', icon: '📧', title: 'CRM e Automação de Marketing', desc: 'Recuperar alunos inativos, mensagens de aniversário, campanhas de retenção.' },
                ].map(feat => (
                  <div key={feat.key} className="feature-toggle-card">
                    <div className="feature-info">
                      <div className="feature-icon">{feat.icon}</div>
                      <div><h4>{feat.title}</h4><p>{feat.desc}</p></div>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={settings[feat.key]} onChange={(e) => setSettings({ ...settings, [feat.key]: e.target.checked })} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                ))}
              </div>
              <button className="btn-primary save-settings-btn" onClick={saveSettings}>💾 Guardar Configurações</button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminAIDashboard;
